import { useCallback, useRef, useState } from "react";
import {
  findFactoryParcelAddressesByPhone,
  FactoryParcelAddressLookupItem,
} from "services/factoryParcelApi";
import {
  normalizePhoneDigits,
  PARCEL_PHONE_MIN_SEARCH_DIGITS,
} from "../destination/parcelPhoneSearchUtils";

export type ParcelAddressLookupResult = {
  items: FactoryParcelAddressLookupItem[];
  /** ตรงกับ request ล่าสุด — ใช้กัน stale response */
  requestId: number;
};

/**
 * ค้นหาที่อยู่จากเบอร์โทร (API factory parcel address lookup)
 * — มี request sequence เพื่อไม่ให้ response เก่าทับผลใหม่
 */
export function useParcelAddressLookup() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<FactoryParcelAddressLookupItem[]>([]);
  const requestSeqRef = useRef(0);

  const lookupByPhone = useCallback(
    async (phone: string): Promise<ParcelAddressLookupResult> => {
      const normalized = normalizePhoneDigits(phone);
      const requestId = ++requestSeqRef.current;

      if (normalized.length < PARCEL_PHONE_MIN_SEARCH_DIGITS) {
        setItems([]);
        return { items: [], requestId };
      }

      setLoading(true);
      try {
        const res = await findFactoryParcelAddressesByPhone(normalized);
        if (requestId !== requestSeqRef.current) {
          return { items: [], requestId };
        }
        const list = res.data?.items ?? [];
        setItems(list);
        return { items: list, requestId };
      } catch {
        if (requestId === requestSeqRef.current) {
          setItems([]);
        }
        return { items: [], requestId };
      } finally {
        if (requestId === requestSeqRef.current) {
          setLoading(false);
        }
      }
    },
    []
  );

  const clear = useCallback(() => {
    requestSeqRef.current += 1;
    setItems([]);
    setLoading(false);
  }, []);

  const cancelInFlight = useCallback(() => {
    requestSeqRef.current += 1;
    setLoading(false);
  }, []);

  return { loading, items, lookupByPhone, clear, cancelInFlight };
}
